import type React from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Box, Heading, Paragraph, Spinner, Text } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';
import type { RentCompareDto } from '@zg-rentals/ts-rental-growth-api-client';

import AutocompleteAreaInput from 'app/shared/components/AutocompleteAreaInput';
import DataCard from 'app/shared/components/DataCard';
import { useMarketTrendsFilters } from 'app/shared/hooks/useFilters';
import { handleCompareMarketChange } from 'app/shared/flux/actions/FilterActions';
import { useAppDispatch } from 'app/shared/flux/hooks';

import constants from 'app/shared/flux/constants';
import filterUtils from 'app/shared/utils/filterUtils';
import numberUtils from 'app/shared/utils/numberUtils';

import { rentPriceStyles, textContainerStyles } from './styles';

import type AutocompleteArea from 'app/shared/models/AutocompleteArea';
import type { Market } from 'app/shared/types/Market';
import type { State } from 'app/shared/types/State';

interface CompareRentProps {
  market: Market;
  isSmallScreen: boolean;
  isMobile: boolean;
}

const CompareRent: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<CompareRentProps>>> = (
  props,
) => {
  const { market, isSmallScreen, isMobile } = props;
  const dispatch = useAppDispatch();
  const {
    filter,
    marketTrends: {
      medianNationalRent,
      rentCompare: { medianRent: otherMedianRent, areaName },
    },
  } = useSelector((state: State) => state);

  const displayAreaName = areaName === 'US' || !areaName ? 'the national average' : areaName;

  const medianRent = otherMedianRent || medianNationalRent || 0;
  const { bedrooms } = useMarketTrendsFilters(filter, dispatch);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rentMultiplier = market.medianRent / medianRent - 1;
  const percentDifference = Math.abs(rentMultiplier * 100);
  const displayPercentage =
    percentDifference < 1
      ? numberUtils.asPercent(percentDifference, '0.00%')
      : numberUtils.asPercent(percentDifference);

  const submit = (area: AutocompleteArea) => {
    async function updateOtherMarket() {
      if (area.areaId) {
        setLoading(true);
        setError(null);
        const resourceId = market.resourceId;
        const otherResourceId = area.resourceId || '';
        try {
          const rentCompare = (await dispatch(
            // @ts-ignore
            handleCompareMarketChange(
              {
                bedrooms: filterUtils.bedroomsEnumToFilter(bedrooms),
                propertyTypes: filter.propertyTypes,
              },
              otherResourceId,
              resourceId,
            ),
          )) as unknown as RentCompareDto | null;

          dispatch({
            type: constants.SET_MARKET_STATS,
            payload: {
              rentCompare: {
                medianRent: rentCompare?.medianRent ?? 0,
                otherResourceId,
                areaName: area.longName,
              },
            },
          });
        } catch (err) {
          setError('An error occurred. Try again.');
          logError({
            error: err,
            errorType: 'CompareRent',
            context: {
              details: `Compare Rent: Error fetching average rent for ${area.longName}`,
              root: 'components/CompareRent#submit',
            },
          });
        } finally {
          setLoading(false);
        }
      }
    }

    updateOtherMarket();
  };

  // Rent in [city/zip/state] is [delta % higher/lower] than the national average.
  return (
    <DataCard
      heading="Compare average rent in markets"
      copyText={
        medianRent === 0 ? (
          `Data is not available for ${displayAreaName}.`
        ) : medianRent === market.medianRent ? (
          `Rent in ${market.name} is equal to rent in ${displayAreaName}.`
        ) : (
          <>
            Rent in {market.name} is <strong>{displayPercentage}</strong> {rentMultiplier >= 0 ? 'higher' : 'lower'}{' '}
            than {displayAreaName}.
          </>
        )
      }
      useFullScreen={isSmallScreen}
    >
      <Box css={textContainerStyles}>
        <Text textStyle={isMobile ? 'body-sm-bold' : 'body-bold'} css={{ width: '45%' }}>
          {market.name}
        </Text>
        <Text textStyle={isMobile ? 'body-sm-bold' : 'body-bold'} css={{ width: '10%' }}>
          vs.
        </Text>
        <Box css={{ padding: '200', width: '45%' }}>
          <AutocompleteAreaInput
            submit={submit}
            type="secondary"
            error={error}
            blockedAreas={[market.resourceId]}
            placeholder="Search"
          />
        </Box>
      </Box>
      <Box
        css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly', flexDirection: 'row', flex: '1' }}
      >
        <Heading textStyle="responsive.heading-lg" level={2} fontFamily="heading-alt" css={rentPriceStyles}>
          {numberUtils.asCurrency(market.medianRent)}
        </Heading>
        <Heading
          textStyle="responsive.heading-lg"
          level={2}
          fontFamily="heading-alt"
          css={{ color: 'aqua.400', width: '50%', textAlign: 'center' }}
        >
          {loading ? (
            <Spinner />
          ) : medianRent !== 0 ? (
            numberUtils.asCurrency(medianRent)
          ) : (
            <>
              --
              <Paragraph textStyle="body-sm" color="text.subtle" css={{ paddingInline: '600' }}>
                Data is not available. Try a different market.
              </Paragraph>
            </>
          )}
        </Heading>
      </Box>
    </DataCard>
  );
};

export default CompareRent;
